import { gsap } from 'gsap';
import Swiper from 'swiper';

export function setupHomeSliders(root: HTMLElement) {
  const compactLayout = window.matchMedia('(max-width: 991.98px)').matches;
  const mobileLayout = window.matchMedia('(max-width: 767.98px)').matches;
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const brandElements = Array.from(
    root.querySelectorAll<HTMLElement>('.at-brand-scroll .at-brand-item .brand')
  );
  const teamSliderElement = root.querySelector<HTMLElement>('.slider-team');
  const teamSlider = teamSliderElement
    ? new Swiper(teamSliderElement, {
        slidesPerView: 1.08,
        slidesPerGroup: 1,
        spaceBetween: 20,
        cssMode: true,
        grabCursor: true,
        watchOverflow: true,
        breakpoints: {
          576: { slidesPerView: 1.5, spaceBetween: 24 },
          768: { slidesPerView: 2, spaceBetween: 24 },
          1200: { slidesPerView: 4, spaceBetween: 24 },
        },
      })
    : null;

  const tickerCleanups: Array<() => void> = [];
  const brandLogoCleanups: Array<() => void> = [];

  brandElements.forEach((brand, brandIndex) => {
    const slides = Array.from(
      brand.querySelectorAll<HTMLElement>(':scope > .brand-logo-slide')
    );
    if (!slides.length || brand.dataset.enhanced === 'true') return;
    // Mobile moves complete partner cards horizontally. Keep one static logo
    // inside each card so no nested vertical animation competes with swiping.
    if (mobileLayout) return;

    brand.dataset.enhanced = 'true';
    const viewport = document.createElement('div');
    viewport.className = 'brand-logo-viewport';
    const track = document.createElement('div');
    track.className = 'brand-logo-track';
    viewport.appendChild(track);
    brand.appendChild(viewport);

    // Every card starts with its authored partner, so all partners are visible
    // together before the staggered rotation begins.
    const orderedSlides = slides;
    orderedSlides.forEach((slide) => track.appendChild(slide));

    const clone = orderedSlides[0]?.cloneNode(true) as HTMLElement | undefined;
    if (clone) {
      clone.dataset.logoClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    const shouldAnimateBrand = !compactLayout && !reducedMotion;
    const timeline = gsap.timeline({
      paused: !shouldAnimateBrand,
      repeat: shouldAnimateBrand ? -1 : 0,
      delay: shouldAnimateBrand ? (brandIndex % 5) * 0.18 : 0,
    });

    if (shouldAnimateBrand) {
      const trackSlideCount = orderedSlides.length + (clone ? 1 : 0);

      orderedSlides.forEach((_, index) => {
        timeline
          .to({}, { duration: 1.15 + (brandIndex % 3) * 0.16 })
          .to(track, {
            yPercent: -((index + 1) * 100) / trackSlideCount,
            duration: 0.82,
            ease: 'power2.inOut',
          });
      });
      timeline.set(track, { yPercent: 0 });
    }

    brandLogoCleanups.push(() => {
      timeline.kill();
      gsap.set(track, { clearProps: 'transform' });
      slides.forEach((slide) => brand.appendChild(slide));
      viewport.remove();
      delete brand.dataset.enhanced;
    });
  });

  const partnerMarquee = mobileLayout
    ? root.querySelector<HTMLElement>('.partner-logo-grid')
    : null;
  const partnerTrack = partnerMarquee?.querySelector<HTMLElement>(
    '.at-brand-scroll-wrap'
  );
  let partnerMarqueeCleanup = () => {};

  if (partnerMarquee && partnerTrack) {
    const originals = Array.from(
      partnerTrack.querySelectorAll<HTMLElement>(':scope > .at-brand-item')
    );

    for (let setIndex = 0; setIndex < 2; setIndex += 1) {
      originals.forEach((item) => {
        const clone = item.cloneNode(true) as HTMLElement;
        clone.dataset.partnerClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        partnerTrack.appendChild(clone);
      });
    }

    const getCycleWidth = () => partnerTrack.scrollWidth / 3;
    partnerMarquee.scrollLeft = getCycleWidth();

    let frame: number | undefined;
    let previousTime = performance.now();
    let isInteracting = false;

    const normalizeScroll = () => {
      const cycleWidth = getCycleWidth();
      if (partnerMarquee.scrollLeft >= cycleWidth * 2) {
        partnerMarquee.scrollLeft -= cycleWidth;
      } else if (partnerMarquee.scrollLeft <= 0) {
        partnerMarquee.scrollLeft += cycleWidth;
      }
    };

    const animate = (time: number) => {
      const deltaSeconds = Math.min((time - previousTime) / 1000, 0.05);
      previousTime = time;
      if (!isInteracting) {
        partnerMarquee.scrollLeft += 34 * deltaSeconds;
        normalizeScroll();
      }
      frame = window.requestAnimationFrame(animate);
    };

    const pause = () => {
      isInteracting = true;
    };
    const resume = () => {
      normalizeScroll();
      previousTime = performance.now();
      isInteracting = false;
    };

    partnerMarquee.addEventListener('pointerdown', pause);
    window.addEventListener('pointerup', resume);
    window.addEventListener('pointercancel', resume);
    if (!reducedMotion) frame = window.requestAnimationFrame(animate);

    partnerMarqueeCleanup = () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      partnerMarquee.removeEventListener('pointerdown', pause);
      window.removeEventListener('pointerup', resume);
      window.removeEventListener('pointercancel', resume);
      partnerTrack
        .querySelectorAll<HTMLElement>('[data-partner-clone="true"]')
        .forEach((clone) => clone.remove());
      partnerMarquee.scrollLeft = 0;
    };
  }

  root.querySelectorAll<HTMLElement>('.carouselTicker').forEach((ticker) => {
    const list = ticker.querySelector<HTMLElement>('.carouselTicker__list');
    if (!list || list.dataset.enhanced === 'true') return;
    if (reducedMotion) return;
    list.dataset.enhanced = 'true';
    const originals = Array.from(list.children);
    originals.forEach((item) => {
      const clone = item.cloneNode(true) as HTMLElement;
      clone.dataset.tickerClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      list.appendChild(clone);
    });
    const direction = ticker.classList.contains('carouselTicker-right')
      ? 1
      : -1;
    const tween = gsap.fromTo(
      list,
      { xPercent: direction > 0 ? -50 : 0 },
      {
        xPercent: direction > 0 ? 0 : -50,
        duration: 42,
        ease: 'none',
        repeat: -1,
      }
    );
    const pause = () => tween.pause();
    const resume = () => tween.resume();
    ticker.addEventListener('mouseenter', pause);
    ticker.addEventListener('mouseleave', resume);
    tickerCleanups.push(() => {
      tween.kill();
      ticker.removeEventListener('mouseenter', pause);
      ticker.removeEventListener('mouseleave', resume);
      list
        .querySelectorAll<HTMLElement>('[data-ticker-clone="true"]')
        .forEach((clone) => clone.remove());
      delete list.dataset.enhanced;
      gsap.set(list, { clearProps: 'transform' });
    });
  });

  return () => {
    teamSlider?.destroy(true, true);
    partnerMarqueeCleanup();
    brandLogoCleanups.forEach((cleanup) => cleanup());
    tickerCleanups.forEach((cleanup) => cleanup());
  };
}
